import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { acceptInvite } from "@/lib/api/invites";
import { login } from "@/lib/api/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AuthFooterDot, AuthLayout } from "@/components/auth/auth-layout";
import { toast } from "@/components/ui/toaster";
import { asset } from "@/lib/asset";

const MIN_PASSWORD = 10;

/** Public page behind an emailed invite link. The token is verified server-side;
 *  nothing here decides whether access is granted. */
export function AcceptInvite() {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    const fd = new FormData(e.currentTarget);
    const password = fd.get("password") as string;
    const confirm = fd.get("confirm") as string;
    const fullName = (fd.get("full_name") as string)?.trim() ?? "";

    if (password.length < MIN_PASSWORD) {
      const msg = `Kies een wachtwoord van minstens ${MIN_PASSWORD} tekens.`;
      setError(msg);
      toast.warning("Wachtwoord te kort", msg);
      return;
    }
    if (password !== confirm) {
      const msg = "De twee wachtwoorden komen niet overeen.";
      setError(msg);
      toast.warning("Wachtwoorden komen niet overeen", msg);
      return;
    }

    setPending(true);
    try {
      const email = await acceptInvite(token, password, fullName);
      // Account exists now — sign straight in so they land on the dashboard.
      const result = await login(email, password);
      if (result.error) {
        toast.success("Account aangemaakt", "Log in met je nieuwe wachtwoord om verder te gaan.");
        navigate("/login", { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    } catch (err) {
      const msg = (err as Error).message;
      setError(msg);
      setPending(false);
      toast.error("Uitnodiging mislukt", msg);
    }
  }

  return (
    <AuthLayout
      footer={
        <>
          <span className="text-xs text-muted-foreground">
            Deze uitnodiging is persoonlijk en verloopt automatisch.
          </span>
          <AuthFooterDot />
          <a href="/managing/" className="text-xs font-medium text-muted-foreground hover:text-primary">
            Naar het influencer-dashboard
          </a>
        </>
      }
    >
      <div className="mb-7 flex flex-col items-center text-center sm:mb-0 sm:pb-6 sm:pt-7">
        <img
          src={asset("favicon.png")}
          alt="Prado"
          className="h-11 w-11 select-none rounded-xl border border-border/60 bg-card object-contain p-1.5 shadow-sm"
          draggable={false}
        />
        <h1 className="mt-4 text-[26px] font-bold leading-tight tracking-tight text-foreground sm:text-[28px]">
          Account aanmaken
        </h1>
        <p className="mt-1.5 text-sm text-muted-foreground">
          Kies een wachtwoord om je toegang tot het Prado-dashboard af te ronden.
        </p>
      </div>

      {error && (
        <Alert variant="error">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="mt-6 space-y-4 sm:mt-0">
        <div className="space-y-2">
          <Label htmlFor="full_name">Naam</Label>
          <Input id="full_name" name="full_name" placeholder="Voor- en achternaam" autoComplete="name" required className="h-12 lg:h-10" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Wachtwoord</Label>
          <PasswordInput
            id="password" name="password" placeholder={`Minstens ${MIN_PASSWORD} tekens`}
            autoComplete="new-password" minLength={MIN_PASSWORD} required
            className="h-12 lg:h-10"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm">Wachtwoord bevestigen</Label>
          <PasswordInput
            id="confirm" name="confirm" placeholder="Herhaal je wachtwoord"
            autoComplete="new-password" minLength={MIN_PASSWORD} required
            className="h-12 lg:h-10"
          />
        </div>
        <Button type="submit" className="h-12 w-full lg:h-10" disabled={pending}>
          {pending ? "Bezig…" : "Account aanmaken"}
        </Button>
      </form>
    </AuthLayout>
  );
}
